import {
  Box,
  FormHelperText,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import deleteGreyIcon from "assets/icons/deleteGreyIcon.svg";
import { Reviewers } from "models/interfaces";
import React, { useEffect } from "react";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import {
  deleteMultipleReviewer,
  selectScreen,
  setContacts,
  setReduxPdfApprovals,
  updateIndividualReviewer,
} from "redux/appSlice";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import { getContactSuggestion } from "../SignRequestHelper";
import { isTruthy } from "helpers/methods";
import ApprovalGroupFields from "./ApprovalGroupFields";
import ApprovalFormFields from "./ApprovalFormFields";
import { normalizeReviewerOrders } from "utils/signerOrderUtils";
import { MoreVert } from "@mui/icons-material";
import DragHorizontalIcon from "../../../assets/images/DragHorizontal.svg"
import plusIcon from "../../../assets/icons/plusIcon.svg";
import approversAndSignersStyles from "../ApproversAndSigners.styles";
import {
  CaretDownGlyph,
  CaretUpGlyph,
  PeopleGlyph,
  PersonGlyph,
  TrashGlyph,
} from "./RecipientsIcons";

const ItemType = {
  APPROVER: "approver",
};

const styles = approversAndSignersStyles;

const ApprovalFrom = (props: {
  allApprovers: Reviewers[];
  eachApprovers: Reviewers;
  contactList: any;
  setContactList: Function;
  handleAdd?: Function;
  index?: number;
  group?: any;
  excludeIds: string[];
  setExcludeIds: Function;
  handleDelete: any;
  isDnDGroup: boolean;
  identity?: string;
}) => {
  const dispatch = useAppDispatch();
  const isMobile = useAppSelector(selectScreen);
  let { signRequest, errors, contacts } = useAppSelector(
    (state: RootState) => state.app,
  );
  const [mobileAnchorEl, setMobileAnchorEl] =
    React.useState<null | HTMLElement>(null);
  let isGroup = props.eachApprovers.group === "" || props.eachApprovers.group;
  const [{ isDragging }, drag, dragPreview] = useDrag({
    type: ItemType.APPROVER,
    item: () => ({
      id: props.eachApprovers.id,
      order: props.eachApprovers.order,
      dragType: props.isDnDGroup ? "GROUP" : "SINGLE",
      groupReviewerIds: props.group?.map((r: any) => r.id) || [],
      groupId: props.identity,
      groupName: props?.eachApprovers?.group,
      index: props.index,
    }),
    // canDrag: () => false,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  useEffect(() => {
    if (!isMobile) return;
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, [dragPreview, isMobile]);

  const opacity = isDragging ? 0 : 1;

  const handleResetContacts = () => {
    if (contacts.length < 10) {
      getContactSuggestion("", dispatch);
      dispatch(setContacts(contacts));
    }
  };

  const handleRemoveContacts = () => {
    props.setContactList((prevContactList: any) => {
      const updatedContactList = Object.keys(prevContactList).reduce(
        (acc: any, key: string) => {
          const { [props.index!]: _, ...restOfContacts } = prevContactList[key];
          return {
            ...acc,
            [key]: restOfContacts,
          };
        },
        {},
      );
      return updatedContactList;
    });
  };

  /**
   * Renaming a group merges it into an existing group of the same name, so every
   * member has to move together.
   */
  const updateGroupName = (previousGroupName: string, newGroupName: string) => {
    const reviewers = signRequest.approval.reviewers;
    if (!newGroupName?.trim()) return;

    const target = reviewers.find(
      (r: Reviewers) =>
        r.group === newGroupName && r.group !== previousGroupName,
    );

    const updatedReviewers = reviewers.map((r: Reviewers) =>
      r.group !== previousGroupName
        ? r
        : target
          ? {
              ...r,
              group: target.group,
              groupId: target.groupId,
              order: target.order,
            }
          : { ...r, group: newGroupName },
    );

    dispatch(setReduxPdfApprovals({ reviewers: updatedReviewers }));
  };

  const handleRemoveApprover = () => {
    props.handleDelete(props.eachApprovers.id, props.eachApprovers.email);

    const remaining: Reviewers[] = props.eachApprovers.group
      ? props.allApprovers.filter((r) => r.group !== props.eachApprovers.group)
      : props.allApprovers.filter((r) => r.id !== props.eachApprovers.id);

    if (!props.eachApprovers.group) handleRemoveContacts();
    dispatch(deleteMultipleReviewer(normalizeReviewerOrders(remaining)));
  };

  /**
   * The design replaces the numeric order box with a pair of chevrons that swap
   * this approval step with its neighbour.
   */
  const stepOrders = Array.from(
    new Set(props.allApprovers.map((r) => Number(r.order))),
  ).sort((a, b) => a - b);
  const stepIndex = stepOrders.indexOf(Number(props.eachApprovers.order));
  const canMoveUp = stepIndex > 0;
  const canMoveDown = stepIndex > -1 && stepIndex < stepOrders.length - 1;

  const moveApprover = (direction: "up" | "down") => {
    const to = direction === "up" ? stepIndex - 1 : stepIndex + 1;
    if (stepIndex < 0 || to < 0 || to >= stepOrders.length) return;

    const fromOrder = stepOrders[stepIndex];
    const targetOrder = stepOrders[to];
    const swapped = props.allApprovers.map((reviewer) => {
      const order = Number(reviewer.order);
      if (order === fromOrder) return { ...reviewer, order: targetOrder };
      if (order === targetOrder) return { ...reviewer, order: fromOrder };
      return reviewer;
    });

    dispatch(deleteMultipleReviewer(normalizeReviewerOrders(swapped as any)));
  };

  return (
    <>
      <Box
        ref={(node: HTMLDivElement | null) => {
          if (!isMobile) {
            drag(node);
          }
        }}
        sx={{ ...(styles.approverCard as object), opacity }}
      >
        <Box sx={styles.approverRow}>
          <Box sx={styles.approverBar} />

          {!isMobile && signRequest.approval?.orderNeeded && (
            <Box sx={styles.approverBadge}>{(props.index ?? 0) + 1}</Box>
          )}

          {!isMobile &&
            (isGroup ? (
              <PeopleGlyph sx={styles.approverTypeIcon} />
            ) : (
              <PersonGlyph sx={styles.approverTypeIcon} />
            ))}

          <Box sx={{ flex: 1, minWidth: 0 }} id={`reviewer-${props.index}`}>
            {isGroup ? (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: "14px" }}
              >
                <Box sx={styles.groupNameField}>
                  <TextField
                    id={`reviewer-${props.index}-group-name`}
                    variant="standard"
                    fullWidth
                    placeholder="Group name (e.g. Legal team)"
                    value={props.eachApprovers.group ?? ""}
                    error={!isTruthy(props.eachApprovers.group) && errors.group}
                    InputProps={{ disableUnderline: true }}
                    inputProps={{
                      "aria-label": "Group name",
                      "aria-required": true,
                    }}
                    onChange={(e) => {
                      const trimmedValue = e.target.value
                        .trimStart()
                        .replace(/(?:^\s+|\s+$|\t+)/g, " ");
                      const previousGroupName = props.eachApprovers.group!;
                      dispatch(
                        updateIndividualReviewer({
                          reviewerId: props.eachApprovers.id,
                          fieldsToUpdate: { group: trimmedValue },
                        }),
                      );
                      updateGroupName(previousGroupName, trimmedValue);
                    }}
                  />
                </Box>
                {/* Groups complete on the first approval, so the rule is fixed. */}
                <Box component="span" sx={styles.approverRule}>
                  <Box component="span" sx={styles.approverRuleDot} />
                  Any one can approve
                </Box>
              </Box>
            ) : (
              <ApprovalFormFields
                eachApprovers={props.eachApprovers}
                index={props.index}
                contactList={props.contactList}
                setContactList={props.setContactList}
                handleRemoveContacts={handleRemoveContacts}
                handleResetContacts={handleResetContacts}
                excludeIds={props.excludeIds}
                setExcludeIds={props.setExcludeIds}
                handleDelete={props.handleDelete}
                isMobile={isMobile}
              />
            )}
          </Box>

          {!isMobile && signRequest.approval?.orderNeeded && (
            <Box sx={styles.rowLevelMoveGroup}>
              <IconButton
                disableRipple
                aria-label="Move approver up"
                disabled={!canMoveUp}
                onClick={() => moveApprover("up")}
                sx={styles.rowLevelMove}
              >
                <CaretUpGlyph sx={styles.rowLevelMoveIcon} />
              </IconButton>
              <IconButton
                disableRipple
                aria-label="Move approver down"
                disabled={!canMoveDown}
                onClick={() => moveApprover("down")}
                sx={styles.rowLevelMove}
              >
                <CaretDownGlyph sx={styles.rowLevelMoveIcon} />
              </IconButton>
            </Box>
          )}

          <Box
            sx={
              isMobile
                ? {
                    display: "flex",
                    flexDirection: "column-reverse",
                    alignItems: "center",
                  }
                : { display: "contents" }
            }
          >
            {!isMobile ? (
              <IconButton
                disableRipple
                id={`delete-signer-${props.eachApprovers.id}-btn`}
                aria-label="Remove approver"
                onClick={handleRemoveApprover}
                sx={styles.rowDelete}
              >
                <TrashGlyph sx={styles.rowDeleteIcon} />
              </IconButton>
            ) : (
              <>
                <IconButton
                  id={`options-${props.eachApprovers.id}-btn`}
                  onClick={(event) => setMobileAnchorEl(event.currentTarget)}
                  size="small"
                  aria-label="More options"
                  aria-haspopup="true"
                  aria-controls={
                    mobileAnchorEl
                      ? `approver-options-menu-${props.eachApprovers.id}`
                      : undefined
                  }
                  aria-expanded={Boolean(mobileAnchorEl)}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
                {isGroup && (
                  <IconButton
                    aria-label="Add member"
                    onClick={() => {
                      getContactSuggestion("", dispatch, props.excludeIds);
                      props.handleAdd!(isGroup, props.eachApprovers.group);
                      if (props.contactList.length < 10) {
                        getContactSuggestion("", dispatch);
                      }
                    }}
                    sx={{ ...(isMobile && { paddingY: 0.5, paddingX: 0 }) }}
                  >
                    <img src={plusIcon} alt="" height={"30px"} />
                  </IconButton>
                )}
                <Menu
                  id={`approver-options-menu-${props.eachApprovers.id}`}
                  anchorEl={mobileAnchorEl}
                  open={Boolean(mobileAnchorEl)}
                  onClose={() => setMobileAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  MenuListProps={{
                    sx: { py: isMobile ? 0 : undefined },
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      setMobileAnchorEl(null);
                      props.handleDelete(props.eachApprovers.email);

                      let remaining: Reviewers[];

                      if (props.eachApprovers.group) {
                        // remove whole group
                        remaining = props.allApprovers.filter(
                          (r) => r.group !== props.eachApprovers.group,
                        );
                      } else {
                        // remove single
                        remaining = props.allApprovers.filter(
                          (r) => r.id !== props.eachApprovers.id,
                        );
                        handleRemoveContacts();
                      }

                      // rebuild correct order structure
                      const normalized = normalizeReviewerOrders(remaining);

                      // single atomic update (no loop dispatch)
                      dispatch(deleteMultipleReviewer(normalized));
                    }}
                    sx={{ fontSize: "0.875rem", padding: "0.5rem 1rem" }}
                  >
                    <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
                      <Box
                        component="img"
                        src={deleteGreyIcon}
                        alt="Delete icon"
                        aria-hidden="true"
                        sx={{ width: "1.25rem" }}
                      />
                    </Box>
                    <Typography variant="subtitle2">Delete</Typography>
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Box>

        {isGroup && (
          <ApprovalGroupFields
            eachApprovers={props.eachApprovers}
            handleAdd={props.handleAdd}
            index={props.index}
            group={props.group}
            contactList={props.contactList}
            setContactList={props.setContactList}
            handleRemoveContacts={handleRemoveContacts}
            handleResetContacts={handleResetContacts}
            excludeIds={props.excludeIds}
            setExcludeIds={props.setExcludeIds}
            handleDelete={props.handleDelete}
            isMobile={isMobile}
          />
        )}

        {isMobile && (
          <Stack
            direction={"row"}
            justifyContent={"center"}
            alignItems="center"
            spacing={1}
            mb={1}
            sx={{ p: 0 }}
          >
            <IconButton
              ref={(node) => {
                drag(node);
              }}
              aria-label="Drag to reorder approver"
              sx={{ p: 0, cursor: "move" }}
            >
              <Box
                component={"img"}
                src={DragHorizontalIcon}
                alt=""
                sx={{ filter: "brightness(0)" }}
              ></Box>
            </IconButton>
          </Stack>
        )}
      </Box>
      {!isTruthy(props.eachApprovers.order) &&
        signRequest.approval.orderNeeded &&
        errors.order && (
          <FormHelperText error role="alert" sx={{ ml: "46px" }}>
            {errors.order}
          </FormHelperText>
        )}
    </>
  );
};

export default ApprovalFrom;
